import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'

// Exact message the backend returns for a 404 on the employee-info lookup
// (see EmployeeService.ts). Consumers key off this specific string to
// distinguish "this employee doesn't exist" from other/transient failures.
export const EMPLOYEE_NOT_FOUND_MESSAGE = 'Employee not found'

// ============================================================================
// EMPLOYEE INFO TYPES
// ============================================================================

export interface EmployeeInfo {
  employeeName: string
  preferredEmail?: string
  primaryApprover: string
  job?: string
  union?: string
  department?: string
  location?: string
  regionName?: string
  shift?: string
  unionSeniorityDate?: string
  alternateSeniorityDate?: string
  kpEmail?: string
  kpSelected?: boolean
  alternateEmail?: string | null
  alternateSelected?: boolean
  /**
   * Employment type ('S' = shift employee). Scoped to the employee the request
   * is being made for — the logged-in user normally, or the selected employee
   * in the Admin Manual Time Off flow.
   */
  empType?: string | null
}

// ============================================================================
// TIME OFF BALANCE TYPES
// ============================================================================

export interface BalanceDetail {
  current: number
  available: number | null
  futureRequests: number
  notAvailable?: boolean
}

export interface BirthdayDetail {
  eligible: boolean
  status: string
}

export interface TimeOffBalance {
  employeeNUID: string
  vacation: BalanceDetail
  float: BalanceDetail
  fpd: BalanceDetail
  birthday: BirthdayDetail | null
}

// ============================================================================
// TIME OFF REQUESTS TYPES
// ============================================================================

export interface TimeOffRequest {
  requestId: string
  todate: string
  hoursRequested: string | number
  timeOffReqstType: string
  status: string
  detail: string
}

export interface TimeOffRequestsResponse {
  data: TimeOffRequest[]
  total: number
  pageNumber: number
  pageSize: number
  totalPages: number
}

// ============================================================================
// CONSOLIDATED STATE TYPE
// ============================================================================

export interface EmployeeInfoState {
  // Employee Info
  employeeInfo: {
    data: EmployeeInfo | null
    loading: boolean
    error: string | null
  }
  // Time Off Balance
  timeOffBalance: {
    data: TimeOffBalance | null
    loading: boolean
    error: string | null
  }
  // Time Off Requests
  timeOffRequests: {
    requests: TimeOffRequest[]
    selectedRequest: TimeOffRequest | null
    currentPage: number
    pageSize: number
    totalRecords: number
    totalPages: number
    sortOrder: 'ASC' | 'DESC'
    loading: boolean
    error: string | null
  }
  // Vacation Bidding
  vacationBidding: {
    requests: TimeOffRequest[]
    currentPage: number
    pageSize: number
    totalRecords: number
    totalPages: number
    sortOrder: 'ASC' | 'DESC'
    loading: boolean
    error: string | null
  }
  // Time Off History (for History tab)
  timeOffHistory: {
    requests: TimeOffRequest[]
    currentPage: number
    pageSize: number
    totalRecords: number
    totalPages: number
    sortOrder: 'ASC' | 'DESC'
    loading: boolean
    error: string | null
  }
}

const initialState: EmployeeInfoState = {
  employeeInfo: {
    data: null,
    loading: false,
    error: null,
  },
  timeOffBalance: {
    data: null,
    loading: false,
    error: null,
  },
  timeOffRequests: {
    requests: [],
    selectedRequest: null,
    currentPage: 1,
    pageSize: 20,
    totalRecords: 0,
    totalPages: 0,
    sortOrder: 'ASC',
    loading: false,
    error: null,
  },
  vacationBidding: {
    requests: [],
    currentPage: 1,
    pageSize: 20,
    totalRecords: 0,
    totalPages: 0,
    sortOrder: 'ASC',
    loading: false,
    error: null,
  },
  timeOffHistory: {
    requests: [],
    currentPage: 1,
    pageSize: 20,
    totalRecords: 0,
    totalPages: 0,
    sortOrder: 'ASC',
    loading: false,
    error: null,
  },
}

// ============================================================================
// ASYNC THUNKS - EMPLOYEE INFO
// ============================================================================

export const getEmployeeInfo = createAsyncThunk(
  'employeeInfo/getEmployeeInfo',
  async (_arg, { rejectWithValue, signal }) => {
    try {
      const options: any = { signal }
      const url = endpoints.requester.getEmployeeInfoDetails
      const response = await api.get(url, options)
      return response.data
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to fetch employee information')
      )
    }
  }
)

export const updateEmployeePreferences = createAsyncThunk(
  'employeeInfo/updateEmployeePreferences',
  async (
    payload: {
      kpSelected: boolean
      alternateSelected: boolean
      alternateEmail?: string
    },
    { rejectWithValue, signal }
  ) => {
    try {
      const options: any = { signal }
      const url = endpoints.requester.getEmployeeInfoDetails
      const response = await api.patch(url, payload, options)
      return response.data
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to update email preferences')
      )
    }
  }
)

// ============================================================================
// ASYNC THUNKS - TIME OFF BALANCE
// ============================================================================

export const getBalance = createAsyncThunk(
  'employeeInfo/getBalance',
  async (_arg, { rejectWithValue, signal }) => {
    try {
      const options: any = { signal }
      const url = endpoints.requester.getBalance
      const response = await api.get(url, options)
      return response.data
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to fetch time off balance')
      )
    }
  }
)

// ============================================================================
// ASYNC THUNKS - ALL REQUESTS (TIME OFF & VACATION BIDDING)
// ============================================================================

export interface GetAllRequestsParams {
  type?: string
  date?: string
  year?: number
  month?: number
  day?: number
  page?: number
  perPage?: number
  timePeriod?: number
  sortOrder?: 'ASC' | 'DESC'
  sortColumn?: 'fromDate' | 'hoursRequested'
}

export const getAllRequests = createAsyncThunk(
  'employeeInfo/getAllRequests',
  async (params: GetAllRequestsParams, { rejectWithValue, signal }) => {
    try {
      const options: any = { signal }

      // Both use year/month/day parameters
      // Distinction: type parameter present = vacationBidding, absent = timeOffRequests
      const now = new Date()
      const year = params.year ?? now.getFullYear()
      const month = params.month ?? now.getMonth() + 1
      const day = params.day ?? now.getDate()
      const url = endpoints.requester.getAllTimeOffReqList({
        type: params.type,
        year,
        month,
        day,
        page: params.page,
        perPage: params.perPage,
        timePeriod: params.timePeriod,
        sortOrder: params.sortOrder,
        sortColumn: params.sortColumn,
      })

      const response = await api.get(url, options)

      return {
        data: response.data?.items || [],
        total: response.data?.meta?.total || 0,
        pageNumber: response.data?.meta?.pageNumber || params.page || 1,
        pageSize: response.data?.meta?.pageSize || params.perPage || 20,
        totalPages: response.data?.meta?.totalPages || 0,
      }
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to fetch requests')
      )
    }
  }
)

// ============================================================================
// SLICE DEFINITION
// ============================================================================

const slice = createSlice({
  name: 'employeeInfo',
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.timeOffRequests.currentPage = action.payload
    },
    setSelectedRequest: (state, action) => {
      state.timeOffRequests.selectedRequest = action.payload
    },
    setVacationBiddingPage: (state, action) => {
      state.vacationBidding.currentPage = action.payload
    },
    setHistoryPage: (state, action) => {
      state.timeOffHistory.currentPage = action.payload
    },
    setTimeOffRequestsSortOrder: (state, action) => {
      state.timeOffRequests.sortOrder = action.payload
    },
    setVacationBiddingSortOrder: (state, action) => {
      state.vacationBidding.sortOrder = action.payload
    },
    setHistorySortOrder: (state, action) => {
      state.timeOffHistory.sortOrder = action.payload
    },
    /**
     * Clears every previously-fetched employee-scoped value (employee info,
     * balance, and the three request lists) without touching pagination/sort
     * preferences. Dispatched whenever the active Manual Time Off employee
     * selection is cleared or changed — the corresponding fetches are
     * already re-triggered by that same change (see EmployeeInfo.tsx,
     * TimeOffBalance.tsx, useFetchAllTabsData.ts), but this closes the
     * window between "selection changed" and "new fetch resolved" during
     * which a stale render could otherwise still show the previous
     * employee's data.
     */
    clearEmployeeScopedData: (state) => {
      state.employeeInfo.data = null
      state.employeeInfo.error = null
      state.timeOffBalance.data = null
      state.timeOffBalance.error = null
      state.timeOffRequests.requests = []
      state.timeOffRequests.selectedRequest = null
      state.timeOffRequests.totalRecords = 0
      state.timeOffRequests.totalPages = 0
      state.timeOffHistory.requests = []
      state.timeOffHistory.totalRecords = 0
      state.timeOffHistory.totalPages = 0
      state.vacationBidding.requests = []
      state.vacationBidding.totalRecords = 0
      state.vacationBidding.totalPages = 0
    },
  },
  extraReducers: (builder) => {
    // ========================================================================
    // EMPLOYEE INFO HANDLERS
    // ========================================================================

    builder.addCase(getEmployeeInfo.pending, (state) => {
      state.employeeInfo.loading = true
      state.employeeInfo.error = null
    })
    builder.addCase(getEmployeeInfo.fulfilled, (state, action) => {
      state.employeeInfo.loading = false
      state.employeeInfo.data = action.payload
    })
    builder.addCase(getEmployeeInfo.rejected, (state, action) => {
      state.employeeInfo.loading = false
      state.employeeInfo.error = action.payload as string
    })

    builder.addCase(updateEmployeePreferences.pending, (state) => {
      state.employeeInfo.loading = true
      state.employeeInfo.error = null
    })
    builder.addCase(updateEmployeePreferences.fulfilled, (state, action) => {
      state.employeeInfo.loading = false
      // Update the local state with the returned data
      if (state.employeeInfo.data) {
        state.employeeInfo.data = {
          ...state.employeeInfo.data,
          ...action.payload,
        }
      }
    })
    builder.addCase(updateEmployeePreferences.rejected, (state, action) => {
      state.employeeInfo.loading = false
      state.employeeInfo.error = action.payload as string
    })

    // ========================================================================
    // TIME OFF BALANCE HANDLERS
    // ========================================================================

    builder.addCase(getBalance.pending, (state) => {
      state.timeOffBalance.loading = true
      state.timeOffBalance.error = null
    })
    builder.addCase(getBalance.fulfilled, (state, action) => {
      state.timeOffBalance.loading = false
      state.timeOffBalance.data = action.payload
    })
    builder.addCase(getBalance.rejected, (state, action) => {
      state.timeOffBalance.loading = false
      state.timeOffBalance.error = action.payload as string
    })

    // ========================================================================
    // ALL REQUESTS HANDLERS (TIME OFF & VACATION BIDDING)
    // ========================================================================

    // For TimeOffRequests (with date parameter)
    builder.addCase(getAllRequests.pending, (state, action) => {
      // History tab: type === 'HISTORY'
      if (action.meta.arg?.type === 'HISTORY') {
        state.timeOffHistory.loading = true
        state.timeOffHistory.error = null
      } else if (action.meta.arg?.type) {
        state.vacationBidding.loading = true
        state.vacationBidding.error = null
      } else {
        state.timeOffRequests.loading = true
        state.timeOffRequests.error = null
      }
    })
    builder.addCase(getAllRequests.fulfilled, (state, action) => {
      const payload = action.payload
      if (action.meta.arg?.type === 'HISTORY') {
        state.timeOffHistory.loading = false
        state.timeOffHistory.error = null
        state.timeOffHistory.requests = payload.data
        state.timeOffHistory.totalRecords = payload.total
        state.timeOffHistory.currentPage = payload.pageNumber
        state.timeOffHistory.pageSize = payload.pageSize
        state.timeOffHistory.totalPages = payload.totalPages
      } else if (action.meta.arg?.type) {
        state.vacationBidding.loading = false
        state.vacationBidding.error = null
        state.vacationBidding.requests = payload.data
        state.vacationBidding.totalRecords = payload.total
        state.vacationBidding.currentPage = payload.pageNumber
        state.vacationBidding.pageSize = payload.pageSize
        state.vacationBidding.totalPages = payload.totalPages
      } else {
        state.timeOffRequests.loading = false
        state.timeOffRequests.error = null
        state.timeOffRequests.requests = payload.data
        state.timeOffRequests.totalRecords = payload.total
        state.timeOffRequests.currentPage = payload.pageNumber
        state.timeOffRequests.pageSize = payload.pageSize
        state.timeOffRequests.totalPages = payload.totalPages
      }
    })
    builder.addCase(getAllRequests.rejected, (state, action) => {
      if (action.meta.arg?.type === 'HISTORY') {
        state.timeOffHistory.loading = false
        state.timeOffHistory.error = action.payload as string
      } else if (action.meta.arg?.type) {
        state.vacationBidding.loading = false
        state.vacationBidding.error = action.payload as string
      } else {
        state.timeOffRequests.loading = false
        state.timeOffRequests.error = action.payload as string
      }
    })
  },
})

export const {
  setCurrentPage,
  setSelectedRequest,
  setVacationBiddingPage,
  setHistoryPage,
  setTimeOffRequestsSortOrder,
  setVacationBiddingSortOrder,
  setHistorySortOrder,
  clearEmployeeScopedData,
} = slice.actions
export default slice.reducer
